// Single Auth module wrapping one PublicClientApplication (MSAL v4, redirect
// flow). All token acquisition for the app goes through here.
import {
  PublicClientApplication,
  EventType,
  LogLevel,
} from '@azure/msal-browser';

let instance = null;
let authConfig = null;

// Redirect loop guard: if acquireTokenSilent keeps failing we must not bounce
// the browser through Microsoft forever. We stamp the time of the last redirect
// per resource and refuse to redirect again within this window.
const REDIRECT_GUARD_PREFIX = 'auth.redirectGuard.';
const REDIRECT_GUARD_WINDOW_MS = 60 * 1000;

function redirectGuardKey(resourceName) {
  return REDIRECT_GUARD_PREFIX + resourceName;
}

function withinRedirectGuard(resourceName) {
  try {
    const raw = sessionStorage.getItem(redirectGuardKey(resourceName));
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    return Number.isFinite(ts) && Date.now() - ts < REDIRECT_GUARD_WINDOW_MS;
  } catch (_) {
    return false;
  }
}

function stampRedirectGuard(resourceName) {
  try {
    sessionStorage.setItem(redirectGuardKey(resourceName), String(Date.now()));
  } catch (_) {
    /* sessionStorage unavailable — best effort */
  }
}

function clearRedirectGuard(resourceName) {
  try {
    sessionStorage.removeItem(redirectGuardKey(resourceName));
  } catch (_) {
    /* ignore */
  }
}

function resolveScopes(resourceName) {
  const res = authConfig && authConfig.resources && authConfig.resources[resourceName];
  if (!res || !Array.isArray(res.scopes) || res.scopes.length === 0) {
    throw new Error(`Auth: unknown or misconfigured resource "${resourceName}".`);
  }
  return res.scopes;
}

// Build MSAL config, initialize (required in MSAL v3+), register the event
// callback, and restore a cached account so sign-in survives reloads.
export async function initAuth(clientId, authority, knownAuthorities, cacheLocation, resources) {
  authConfig = { clientId, authority, knownAuthorities, cacheLocation, resources };

  instance = new PublicClientApplication({
    auth: {
      clientId,
      authority,
      knownAuthorities,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation,
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        piiLoggingEnabled: false,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return; // never surface PII
          if (level === LogLevel.Error) console.error('[MSAL]', message);
          else if (level === LogLevel.Warning) console.warn('[MSAL]', message);
          // Info/Verbose intentionally dropped.
        },
      },
    },
  });

  await instance.initialize();
  // Resolve any redirect response before the app renders so a returning user is
  // signed in immediately.
  await instance.handleRedirectPromise();

  instance.addEventCallback((event) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      event.payload &&
      event.payload.account
    ) {
      instance.setActiveAccount(event.payload.account);
    }
  });

  const accounts = instance.getAllAccounts();
  if (accounts.length > 0 && !instance.getActiveAccount()) {
    instance.setActiveAccount(accounts[0]);
  }

  return instance;
}

export function getMsalInstance() {
  return instance;
}

export function getAuthConfig() {
  return authConfig;
}

// Login request object for a given resource — used by MsalAuthenticationTemplate
// and any explicit loginRedirect.
export function loginRequestFor(resourceName = 'api') {
  return { scopes: resolveScopes(resourceName) };
}

// Acquire an access token for the named resource. Silent first; on failure fall
// back to an interactive redirect, guarded against redirect loops.
export async function getAccessToken(resourceName = 'api') {
  if (!instance) throw new Error('Auth: getAccessToken called before initAuth.');
  const scopes = resolveScopes(resourceName);
  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];

  if (account) {
    try {
      const result = await instance.acquireTokenSilent({ scopes, account });
      clearRedirectGuard(resourceName); // silent path healthy again
      return result.accessToken;
    } catch (_) {
      // fall through to interactive redirect
    }
  }

  if (withinRedirectGuard(resourceName)) {
    throw new Error(
      `Auth: silent token acquisition for "${resourceName}" keeps failing; ` +
        'suppressing repeat redirect to avoid a redirect loop.'
    );
  }
  stampRedirectGuard(resourceName);
  await instance.acquireTokenRedirect({ scopes, account });
  return null; // the page is navigating away to Microsoft
}

export async function loginRedirect(resourceName = 'api') {
  if (!instance) throw new Error('Auth: loginRedirect called before initAuth.');
  return instance.loginRedirect(loginRequestFor(resourceName));
}

export async function logout() {
  if (!instance) return;
  const account = instance.getActiveAccount();
  await instance.logoutRedirect({ postLogoutRedirectUri: '/', account });
  instance.setActiveAccount(null);
}

// Roles from the ID token — for UI gating ONLY. The backend authorizes
// independently and is the real gate.
export function getRoles() {
  const account = instance && instance.getActiveAccount();
  const roles = account && account.idTokenClaims && account.idTokenClaims.roles;
  return Array.isArray(roles) ? roles : [];
}

export function hasRole(role) {
  return getRoles().includes(role);
}
